import { useRef } from "react";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { getSpecialties } from "@/services/sintegrada/specialty/getSpecialties";
import { Option } from "@/@types/generics/Option";
import { filterOptions } from "@/utils/filterOptions";

import { SelectComponent, SelectComponentProps } from "@/components/SelectComponent";

type Props = SelectComponentProps;

export type MedicalAttendanceDepartmentValue = {
	department: string;
	specialtyId: number | null;
};

export type MedicalAttendanceDepartmentOption = Option<MedicalAttendanceDepartmentValue>;

export const defaultMedicalAttendanceDepartmentOption: MedicalAttendanceDepartmentOption = {
	label: "Consultório Médico",
	value: {
		department: "consultation",
		specialtyId: null,
	},
};

export function MedicalAttendanceDepartmentSelect({ ...rest }: Props) {
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const loadedOptions = useRef<Option<MedicalAttendanceDepartmentValue>[]>([]);

	async function loadOptions(inputValue: string) {
		const hasLoadedOptions = Boolean(loadedOptions.current.length);

		if (hasLoadedOptions) {
			return filterOptions(inputValue, loadedOptions.current);
		}

		const specialties = await getSpecialties({
			uf,
			ibgeCode,
			cnes,
		});

		const departmentOptions = [
			defaultMedicalAttendanceDepartmentOption,
			{ label: "Internamento", value: { department: "internment", specialtyId: null } },
			{ label: "Sala Vermelha", value: { department: "red-room", specialtyId: null } },
			{ label: "Observação", value: { department: "observation", specialtyId: null } },
		];

		const specialtyOptions = specialties.map((specialty) => ({
			label: `Especialidade - ${specialty.name}`,
			value: {
				department: "specialty-consultation",
				specialtyId: specialty.id,
			},
		}));

		const options = [
			...departmentOptions,
			...specialtyOptions,
		];

		loadedOptions.current = options;

		return options;
	}

	return (
		<SelectComponent
			isAsync
			defaultOptions
			placeholder="Selecione..."
			loadOptions={loadOptions}
			{...rest}
		/>
	);
}